import React, { useCallback, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  AQL_SELECTABLE_VALUES,
  normalizeInspectionLevel,
  normalizeInspectionMode,
  planQcSampling,
  type QcInspectionMode,
} from "@/lib/aql";

export interface QcSamplingValues {
  lotSize: number;
  mode: QcInspectionMode;
  level: string;
  aqlMajor: number;
  aqlMinor: number | null;
  /** Cách kiểm 100%: true = loại khi có mọi lỗi; false = chỉ lỗi nặng trở lên */
  fullCountMinor: boolean;
  sampleSize: number;
  acMajor: number;
  reMajor: number;
  acMinor: number | null;
  reMinor: number | null;
}

export interface QcSamplingState extends QcSamplingValues {
  setLotSize: (v: number) => void;
  setMode: (v: QcInspectionMode) => void;
  setLevel: (v: string) => void;
  setAqlMajor: (v: number) => void;
  setAqlMinor: (v: number | null) => void;
  setFullCountMinor: (v: boolean) => void;
}

type SamplingInit = Partial<QcSamplingValues> & { lotSize: number };

function withPlan(v: Omit<QcSamplingValues, "sampleSize" | "acMajor" | "reMajor" | "acMinor" | "reMinor">): QcSamplingValues {
  const plan = planQcSampling({
    lotSize: v.lotSize,
    mode: v.mode,
    level: v.level,
    aqlMajor: v.aqlMajor,
    aqlMinor: v.aqlMinor,
    fullCountMinor: v.fullCountMinor,
  });
  return {
    ...v,
    sampleSize: plan.sampleSize,
    acMajor: plan.acMajor,
    reMajor: plan.reMajor,
    acMinor: plan.acMinor,
    reMinor: plan.reMinor,
  };
}

/**
 * Trạng thái khối Lấy mẫu. Cỡ mẫu, Ac/Re tự tra bảng mỗi khi đổi đầu vào.
 * Phiếu cũ (recomputeOnMount = false) giữ số đã lưu cho tới khi người dùng đổi một đầu vào.
 */
export function useQcSamplingState(init: SamplingInit, options: { recomputeOnMount: boolean }): QcSamplingState {
  const [values, setValues] = useState<QcSamplingValues>(() => {
    const mode = normalizeInspectionMode(init.mode);
    const base = {
      lotSize: Math.max(0, Math.round(Number(init.lotSize) || 0)),
      mode,
      level: normalizeInspectionLevel(init.level),
      aqlMajor: Number(init.aqlMajor) > 0 ? Number(init.aqlMajor) : 1.5,
      aqlMinor: init.aqlMinor === null ? null : Number(init.aqlMinor) > 0 ? Number(init.aqlMinor) : 4.0,
      fullCountMinor: init.fullCountMinor ?? !(mode === "FULL" && (init.reMinor === null || init.reMinor === undefined)),
    };
    if (options.recomputeOnMount) return withPlan(base);
    const computed = withPlan(base);
    return {
      ...base,
      sampleSize: init.sampleSize ?? computed.sampleSize,
      acMajor: init.acMajor ?? computed.acMajor,
      reMajor: init.reMajor ?? computed.reMajor,
      acMinor: init.acMinor === undefined ? null : init.acMinor,
      reMinor: init.reMinor === undefined ? null : init.reMinor,
    };
  });

  const update = useCallback((patch: Partial<QcSamplingValues>) => {
    setValues((prev) => withPlan({ ...prev, ...patch }));
  }, []);

  return {
    ...values,
    setLotSize: (v) => update({ lotSize: Math.max(0, Math.round(Number(v) || 0)) }),
    setMode: (v) => update({ mode: v }),
    setLevel: (v) => update({ level: normalizeInspectionLevel(v) }),
    setAqlMajor: (v) => update({ aqlMajor: Number(v) || 1.5 }),
    setAqlMinor: (v) => update({ aqlMinor: v }),
    setFullCountMinor: (v) => update({ fullCountMinor: v }),
  };
}

const LEVEL_OPTIONS = [
  { value: "I", label: "I – Giảm" },
  { value: "II", label: "II – Thường" },
  { value: "III", label: "III – Chặt" },
];

function aqlLabel(v: number) {
  return Number.isInteger(v) ? v.toFixed(1) : String(v);
}

function Cell({ label, htmlFor, children, className }: { label: string; htmlFor?: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("min-w-0 space-y-1", className)}>
      <Label htmlFor={htmlFor} className="text-[11px] font-semibold text-slate-600 whitespace-nowrap">
        {label}
      </Label>
      {children}
    </div>
  );
}

/** Ô kết quả chỉ đọc (cỡ mẫu, Ac/Re) */
function ResultBox({ id, value, title }: { id: string; value: string; title?: string }) {
  return (
    <Input
      id={id}
      readOnly
      tabIndex={-1}
      value={value}
      title={title}
      aria-readonly="true"
      className="h-8 cursor-default border-slate-200 bg-slate-100 text-xs font-mono font-bold text-slate-900 focus-visible:ring-0"
    />
  );
}

interface SamplingRowProps {
  sampling: QcSamplingState;
  idPrefix: string;
  unit?: string;
  onUnitChange?: (v: string) => void;
  /** Gọi thêm khi đổi cỡ lô (VD: tính lại SL nhập kho) */
  onLotSizeChange?: (v: number) => void;
  lotSizeReadOnly?: boolean;
  readOnly?: boolean;
}

/**
 * Một hàng "Lấy mẫu": Cỡ lô · ĐVT · Cách kiểm · Mức kiểm · AQL nặng · AQL nhẹ · Cỡ mẫu · Ac/Re nặng · Ac/Re nhẹ.
 * Cỡ mẫu và Ac/Re là kết quả tra bảng ISO 2859-1, chỉ đọc. Lỗi nghiêm trọng luôn Ac = 0 nên không có ô.
 */
export function SamplingRow({ sampling: s, idPrefix, unit, onUnitChange, onLotSizeChange, lotSizeReadOnly, readOnly }: SamplingRowProps) {
  const isFull = s.mode === "FULL";
  const id = (k: string) => `${idPrefix}-${k}`;
  return (
    <div className="@container min-w-0 space-y-1.5" data-testid="qc-sampling-row">
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Lấy mẫu (ISO 2859-1)</div>
      <div className="grid grid-cols-2 gap-2 @md:grid-cols-3 @2xl:grid-cols-5 @5xl:grid-cols-9">
        <Cell label="Cỡ lô" htmlFor={id("lot")}>
          <Input
            id={id("lot")}
            type="number"
            min={0}
            value={s.lotSize || 0}
            readOnly={readOnly || lotSizeReadOnly}
            onChange={(e) => {
              const v = Math.max(0, Math.round(Number(e.target.value) || 0));
              s.setLotSize(v);
              onLotSizeChange?.(v);
            }}
            className="h-8 text-xs font-mono font-bold"
          />
        </Cell>
        {onUnitChange !== undefined || unit !== undefined ? (
          <Cell label="ĐVT" htmlFor={id("unit")}>
            <Input
              id={id("unit")}
              value={unit || ""}
              readOnly={readOnly || !onUnitChange}
              onChange={(e) => onUnitChange?.(e.target.value)}
              placeholder="Tờ / Kg / Cái"
              className="h-8 text-xs font-semibold"
            />
          </Cell>
        ) : null}
        <Cell label="Cách kiểm" htmlFor={id("mode")}>
          <Select value={s.mode} onValueChange={(v) => s.setMode(v as QcInspectionMode)} disabled={readOnly}>
            <SelectTrigger id={id("mode")} className="h-8 w-full min-w-0 text-xs font-semibold">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="AQL">AQL</SelectItem>
              <SelectItem value="FULL">100%</SelectItem>
            </SelectContent>
          </Select>
        </Cell>
        {isFull ? (
          <Cell label="Lỗi bị loại" htmlFor={id("full-class")} className="@2xl:col-span-3">
            <Select
              value={s.fullCountMinor ? "ALL" : "MAJOR"}
              onValueChange={(v) => s.setFullCountMinor(v === "ALL")}
              disabled={readOnly}
            >
              <SelectTrigger id={id("full-class")} className="h-8 w-full min-w-0 text-xs font-semibold">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Mọi lỗi (nghiêm trọng, nặng, nhẹ)</SelectItem>
                <SelectItem value="MAJOR">Nặng trở lên (bỏ qua lỗi nhẹ)</SelectItem>
              </SelectContent>
            </Select>
          </Cell>
        ) : (
          <>
            <Cell label="Mức kiểm" htmlFor={id("level")}>
              <Select value={s.level} onValueChange={s.setLevel} disabled={readOnly}>
                <SelectTrigger id={id("level")} className="h-8 w-full min-w-0 text-xs font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEVEL_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Cell>
            <Cell label="AQL nặng" htmlFor={id("aql-major")}>
              <Select value={String(s.aqlMajor)} onValueChange={(v) => s.setAqlMajor(Number(v))} disabled={readOnly}>
                <SelectTrigger id={id("aql-major")} className="h-8 w-full min-w-0 text-xs font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AQL_SELECTABLE_VALUES.map((v) => (
                    <SelectItem key={v} value={String(v)}>
                      {aqlLabel(v)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Cell>
            <Cell label="AQL nhẹ" htmlFor={id("aql-minor")}>
              <Select
                value={s.aqlMinor === null ? "NONE" : String(s.aqlMinor)}
                onValueChange={(v) => s.setAqlMinor(v === "NONE" ? null : Number(v))}
                disabled={readOnly}
              >
                <SelectTrigger id={id("aql-minor")} className="h-8 w-full min-w-0 text-xs font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">Không xét</SelectItem>
                  {AQL_SELECTABLE_VALUES.map((v) => (
                    <SelectItem key={v} value={String(v)}>
                      {aqlLabel(v)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Cell>
          </>
        )}
        <Cell label="Cỡ mẫu" htmlFor={id("sample")}>
          <ResultBox id={id("sample")} value={String(s.sampleSize || 0)} title={isFull ? "Kiểm 100% lô" : "Tra bảng ISO 2859-1"} />
        </Cell>
        <Cell label="Ac/Re nặng" htmlFor={id("acre-major")}>
          <ResultBox id={id("acre-major")} value={`${s.acMajor} / ${s.reMajor}`} />
        </Cell>
        <Cell label="Ac/Re nhẹ" htmlFor={id("acre-minor")}>
          <ResultBox id={id("acre-minor")} value={s.reMinor === null || s.reMinor === undefined ? "—" : `${s.acMinor} / ${s.reMinor}`} />
        </Cell>
      </div>
    </div>
  );
}

export interface QcSamplingSummaryProps {
  lotSize?: number | null;
  unit?: string | null;
  mode?: string | null;
  level?: string | null;
  aqlMajor?: number | null;
  aqlMinor?: number | null;
  sampleSize?: number | null;
  acMajor?: number | null;
  reMajor?: number | null;
  acMinor?: number | null;
  reMinor?: number | null;
}

/** Hàng "Lấy mẫu" chỉ đọc (trang xem phiếu) */
export function QcSamplingSummary(p: QcSamplingSummaryProps) {
  const isFull = String(p.mode || "").toUpperCase() === "FULL";
  const fmt = (v: any) => (v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString("vi-VN"));
  const cells: Array<[string, string]> = [
    ["Cỡ lô", `${fmt(p.lotSize)}${p.unit ? ` ${p.unit}` : ""}`],
    ["Cách kiểm", isFull ? "100%" : "AQL"],
    ...(isFull
      ? ([["Lỗi bị loại", p.reMinor ? "Mọi lỗi" : "Nặng trở lên"]] as Array<[string, string]>)
      : ([
          ["Mức kiểm", p.level || "II"],
          ["AQL nặng", p.aqlMajor ? String(p.aqlMajor) : "—"],
          ["AQL nhẹ", p.aqlMinor ? String(p.aqlMinor) : "—"],
        ] as Array<[string, string]>)),
    ["Cỡ mẫu", fmt(p.sampleSize)],
    ["Ac/Re nặng", p.reMajor ? `${p.acMajor ?? 0} / ${p.reMajor}` : "—"],
    ["Ac/Re nhẹ", p.reMinor ? `${p.acMinor ?? 0} / ${p.reMinor}` : "—"],
  ];
  return (
    <div className="@container min-w-0 space-y-1.5" data-testid="qc-sampling-row">
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Lấy mẫu (ISO 2859-1)</div>
      <div className="grid grid-cols-2 gap-2 @md:grid-cols-4 @4xl:grid-cols-8">
        {cells.map(([label, value]) => (
          <div key={label} className="min-w-0 rounded-md border border-slate-200 bg-slate-50 px-2 py-1">
            <div className="text-[10px] font-semibold text-slate-500">{label}</div>
            <div className="truncate text-xs font-mono font-bold text-slate-900">{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
